import React, {
  createContext, useCallback, useContext, useEffect, useRef, useState,
} from 'react';

const ConfirmCtx = createContext(null);

export function ConfirmProvider({ children }) {
  const [state, setState] = useState(null); // { opts, resolve } or null
  const resolveRef = useRef(null);

  const confirm = useCallback((opts) => {
    // Accept a plain string for parity with window.confirm
    const normalized = typeof opts === 'string' ? { message: opts } : (opts || {});
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ opts: normalized });
    });
  }, []);

  const close = useCallback((result) => {
    if (resolveRef.current) {
      resolveRef.current(result);
      resolveRef.current = null;
    }
    setState(null);
  }, []);

  // Close on Escape; confirm on Enter.
  useEffect(() => {
    if (!state) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') close(false);
      else if (e.key === 'Enter') close(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [state, close]);

  return (
    <ConfirmCtx.Provider value={confirm}>
      {children}
      {state && (
        <ConfirmDialog
          opts={state.opts}
          onCancel={() => close(false)}
          onConfirm={() => close(true)}
        />
      )}
    </ConfirmCtx.Provider>
  );
}

export function useConfirm() {
  const fn = useContext(ConfirmCtx);
  if (!fn) {
    throw new Error('useConfirm must be used inside a <ConfirmProvider>');
  }
  return fn;
}

function ConfirmDialog({ opts, onCancel, onConfirm }) {
  const {
    title = 'Are you sure?',
    message = '',
    confirmLabel = 'Confirm',
    cancelLabel = 'Cancel',
    danger = false,
  } = opts;

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0,
        background: 'rgba(0, 0, 0, 0.65)',
        backdropFilter: 'blur(4px)',
        zIndex: 9999,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%', maxWidth: 420,
          background: '#161618',
          border: '1px solid rgba(212, 181, 116, 0.25)',
          borderRadius: 14,
          padding: '22px 22px 18px',
          boxShadow: '0 18px 60px rgba(0, 0, 0, 0.5)',
        }}
      >
        <div style={{
          color: '#F7E8BF',
          fontSize: 17,
          fontWeight: 800,
          letterSpacing: 0.2,
          marginBottom: 10,
        }}>
          {title}
        </div>
        {message && (
          <div style={{
            color: '#D8C8A4',
            fontSize: 13,
            lineHeight: 1.55,
            whiteSpace: 'pre-wrap',
            marginBottom: 22,
          }}>
            {message}
          </div>
        )}
        <div style={{
          display: 'flex',
          justifyContent: 'flex-end',
          gap: 10,
        }}>
          <button
            type="button"
            onClick={onCancel}
            style={{
              padding: '9px 18px',
              background: 'transparent',
              color: '#D8C8A4',
              border: '1px solid rgba(212, 181, 116, 0.3)',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: 0.3,
              cursor: 'pointer',
            }}
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            autoFocus
            style={{
              padding: '9px 22px',
              background: danger
                ? 'linear-gradient(135deg, #F87171, #E63946)'
                : 'linear-gradient(135deg, #F7E8BF, #D4B574)',
              color: danger ? '#FFFFFF' : '#000000',
              border: 'none',
              borderRadius: 8,
              fontSize: 13,
              fontWeight: 800,
              letterSpacing: 0.4,
              cursor: 'pointer',
            }}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
